/**
 * Platform invitation request/response schemas — single source of truth shared
 * by the backend NestJS DTOs and the frontend admin portal.
 *
 * BE endpoints:
 *   POST /admin/staff                           — body { email, role } → 201 { invitationId, email, role, status }
 *   POST /admin/staff/invitations/:id/resend    — 200
 *   POST /admin/staff/invitations/:id/revoke    — 200
 *   POST /auth/platform-invitations/preview     — body { token } → 200 { email, platformRole, expiresAt }
 *   POST /auth/platform-invitations/accept      — body { token, password?, firstName?, lastName? } → 200 { wasNewAccount }
 *
 * Both public endpoints are anti-enum on token failures: ANY token-invalid
 * cause (unknown / wrong hash / expired / revoked / already-accepted /
 * concurrent-modification) returns a BYTE-IDENTICAL 401 with body
 * `{ error: 'invitation_token_invalid' }`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Admin — POST /admin/staff (create invitation)
// ---------------------------------------------------------------------------
export const createPlatformInvitationBodySchema = z
    .object({
    email: z.string().email(),
    role: z.enum(['root', 'support']),
})
    .strict();
export const createPlatformInvitationResponseSchema = z
    .object({
    invitationId: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(['root', 'support']),
    status: z.literal('invited'),
})
    .strict();
// ---------------------------------------------------------------------------
// Public — POST /auth/platform-invitations/preview
// ---------------------------------------------------------------------------
export const previewPlatformInvitationBodySchema = z
    .object({
    token: z.string().min(1),
})
    .strict();
export const previewPlatformInvitationResponseSchema = z
    .object({
    email: z.string().email(),
    platformRole: z.enum(['root', 'support']),
    expiresAt: z.string(),
})
    .strict();
// ---------------------------------------------------------------------------
// Public — POST /auth/platform-invitations/accept
// ---------------------------------------------------------------------------
/**
 * Name field rule — mirrors the org invitation name field.
 * Optional at the wire boundary; the FE wizard may enforce required client-side.
 */
const PLATFORM_INVITATION_NAME_MAX_LENGTH = 100;
export const acceptPlatformInvitationBodySchema = z
    .object({
    token: z.string().min(1),
    password: z.string().min(12).max(128).optional(),
    firstName: z
        .string()
        .min(1)
        .max(PLATFORM_INVITATION_NAME_MAX_LENGTH)
        .optional(),
    lastName: z
        .string()
        .min(1)
        .max(PLATFORM_INVITATION_NAME_MAX_LENGTH)
        .optional(),
})
    .strict();
export const acceptPlatformInvitationResponseSchema = z
    .object({
    wasNewAccount: z.boolean(),
})
    .strict();
//# sourceMappingURL=platform-invitation.js.map